/**
 * Generic Base Component
 *
 * SVG component for player home base area.
 * Features a lime-green outer frame with 3D shadow, inner colored section
 * with rounded corners, dark border, and 4 pawn spots.
 *
 * This component is color-agnostic and accepts a colorScheme prop to render
 * bases for different players (Red, Blue, Yellow, Green).
 */

import React, { type ReactElement } from 'react';
import type { BaseProps } from '../../constants/types';
import type { PawnProps } from '../../constants/types';
import { renderPawnAtPosition } from './utils/utils';
import type { BaseColorScheme } from './constants/baseColorSchemes';

// Helper constants for rank display
const RANK_ICONS = ['🏆', '🥈', '🥉', '🏅'] as const;
const RANK_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

function getRankIcon(rank: number): string {
  return RANK_ICONS[Math.min(rank - 1, 3)];
}

function getRankOrdinal(rank: number): string {
  if (rank >= 1 && rank <= 4) {
    return RANK_ORDINALS[rank - 1];
  }
  return `${rank.toString()}th`;
}

export interface BaseComponentProps extends BaseProps {
  colorScheme: BaseColorScheme;
  PawnComponent: React.ComponentType<PawnProps>;
  onPawn1Click?: () => void;
  onPawn2Click?: () => void;
  onPawn3Click?: () => void;
  onPawn4Click?: () => void;
  shouldAnimatePawns?: boolean;
  /** Player's finishing rank (1-4) - when set, shows rank badge instead of pawns */
  playerRank?: number;
  /** Whether the player has voluntarily left the game */
  hasLeft?: boolean;
}

const BaseComponent = React.memo(function BaseComponent({
  viewBox = '0 0 486 523',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  showEntrance = false,
  pawn1 = false,
  pawn2 = false,
  pawn3 = false,
  pawn4 = false,
  pawn1Active = false,
  pawn1Hovered = false,
  pawn1Selected = false,
  pawn1Moveable = false,
  pawn2Active = false,
  pawn2Hovered = false,
  pawn2Selected = false,
  pawn2Moveable = false,
  pawn3Active = false,
  pawn3Hovered = false,
  pawn3Selected = false,
  pawn3Moveable = false,
  pawn4Active = false,
  pawn4Hovered = false,
  pawn4Selected = false,
  pawn4Moveable = false,
  colorScheme,
  PawnComponent,
  onPawn1Click,
  onPawn2Click,
  onPawn3Click,
  onPawn4Click,
  shouldAnimatePawns = false,
  playerRank,
  hasLeft = false
}: BaseComponentProps): ReactElement {
  const {
    name,
    ariaLabel,
    frame,
    innerColor1,
    innerColor2,
    innerColor3,
    pawnSpot,
    shadowBlack,
    grassGreen
  } = colorScheme;

  const svgClassName = [
    className,
    showEntrance ? 'base-entrance' : '',
    isActive ? 'base-active' : '',
    isHovered ? 'base-hovered' : '',
    isSelected ? 'base-selected' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const gradientIdPrefix = `${name}Base`;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient
          id={`${gradientIdPrefix}Shadow1`}
          x1="242.64"
          y1="432.92"
          x2="242.64"
          y2="454.44"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72381B" />
          <stop offset="1" stopColor="#754C24" />
        </linearGradient>
        <linearGradient
          id={`${gradientIdPrefix}Shadow2`}
          x1="242.66"
          y1="436.14"
          x2="242.66"
          y2="412.18"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4B9E36" />
          <stop offset="1" stopColor="#8CC63F" />
        </linearGradient>
      </defs>

      {/* Shadow layers (3D depth effect) - opacity 0.12 */}
      <g opacity="0.12">
        <path
          d="M46.79 416.29V449.78C46.79 459.58 52.04 468.73 60.67 473.37C60.74 473.41 60.81 473.44 60.88 473.48C70.89 478.77 80.34 477.55 91.75 477.02C105.66 476.37 119.58 478.42 133.51 478.42C161.26 478.42 189.02 478.46 216.77 478.42C236.72 478.39 256.37 478.41 270.42 478.42C291.94 478.44 302.7 478.45 303.83 478.47C351.11 479.21 354.88 482.41 371.65 478.47C383.25 475.75 387.4 472.83 398.22 472.98C408.48 473.12 415.05 475.89 423.53 472.47C430.46 469.68 435.42 463.11 435.42 463.11C435.43 463.09 435.45 463.08 435.46 463.06C437.47 460.72 438.53 457.71 438.5 454.63C438.36 440.08 438.22 425.53 438.08 410.98C375.55 420.37 302.93 427.1 222.13 427.05C157.88 427.01 99.09 422.7 46.79 416.29Z"
          fill={shadowBlack}
        />
        <path
          d="M46.79 392.67V436.87C49.48 440.85 53.93 446.19 60.88 449.86C70.89 455.15 80.34 453.93 91.75 453.4C105.66 452.75 119.58 454.8 133.51 454.8C161.26 454.8 189.02 454.84 216.77 454.8C236.72 454.77 256.37 454.79 270.42 454.8C291.94 454.82 302.7 454.83 303.83 454.85C351.11 455.59 354.88 458.79 371.65 454.85C383.25 452.13 387.4 449.21 398.22 449.36C408.48 449.5 415.05 452.27 423.53 448.85C430.46 446.06 435.42 439.49 435.42 439.49C436.81 437.88 437.85 436.42 438.54 435.36C438.39 419.36 438.23 403.36 438.08 387.36C375.55 396.75 302.93 403.48 222.13 403.43C157.88 403.39 99.09 399.08 46.79 392.67Z"
          fill={shadowBlack}
        />
        <path
          d="M401.65 62.3301H83.68C63.3062 62.3301 46.79 78.8463 46.79 99.2201V399.99C46.79 420.364 63.3062 436.88 83.68 436.88H401.65C422.024 436.88 438.54 420.364 438.54 399.99V99.2201C438.54 78.8463 422.024 62.3301 401.65 62.3301Z"
          fill={shadowBlack}
        />
        <path
          d="M367.48 95H117.84C97.4662 95 80.95 111.516 80.95 131.89V367.32C80.95 387.694 97.4662 404.21 117.84 404.21H367.48C387.854 404.21 404.37 387.694 404.37 367.32V131.89C404.37 111.516 387.854 95 367.48 95Z"
          fill={shadowBlack}
        />
        <path
          d="M368.71 102.81H116.61C101.428 102.81 89.12 115.118 89.12 130.3V368.91C89.12 384.092 101.428 396.4 116.61 396.4H368.71C383.892 396.4 396.2 384.092 396.2 368.91V130.3C396.2 115.118 383.892 102.81 368.71 102.81Z"
          fill={shadowBlack}
        />
      </g>

      {/* Bottom gradient shadow (3D effect) */}
      <path
        d="M46.79 395.86V429.35C46.79 439.15 52.04 448.3 60.67 452.94C60.74 452.98 60.81 453.01 60.88 453.05C70.89 458.34 80.34 457.12 91.75 456.59C105.66 455.94 119.58 457.99 133.51 457.99C161.26 457.99 189.02 458.03 216.77 457.99C236.72 457.96 256.37 457.98 270.42 457.99C291.94 458.01 302.7 458.02 303.83 458.04C351.11 458.78 354.88 461.98 371.65 458.04C383.25 455.32 387.4 452.4 398.22 452.55C408.48 452.69 415.05 455.46 423.53 452.04C430.46 449.25 435.42 442.68 435.42 442.68C435.43 442.66 435.45 442.65 435.46 442.63C437.47 440.29 438.53 437.28 438.5 434.2C438.36 419.65 438.22 405.1 438.08 390.55C375.55 399.94 302.93 406.67 222.13 406.62C157.88 406.58 99.09 402.27 46.79 395.86Z"
        fill={`url(#${gradientIdPrefix}Shadow1)`}
      />

      {/* Frame gradient shadow (3D depth) */}
      <path
        d="M46.79 372.24V416.44C49.48 420.42 53.93 425.76 60.88 429.43C70.89 434.72 80.34 433.5 91.75 432.97C105.66 432.32 119.58 434.37 133.51 434.37C161.26 434.37 189.02 434.41 216.77 434.37C236.72 434.34 256.37 434.36 270.42 434.37C291.94 434.39 302.7 434.4 303.83 434.42C351.11 435.16 354.88 438.36 371.65 434.42C383.25 431.7 387.4 428.78 398.22 428.93C408.48 429.07 415.05 431.84 423.53 428.42C430.46 425.63 435.42 419.06 435.42 419.06C436.81 417.45 437.85 415.99 438.54 414.93C438.39 398.93 438.23 382.93 438.08 366.93C375.55 376.32 302.93 383.05 222.13 383C157.88 382.96 99.09 378.65 46.79 372.24Z"
        fill={`url(#${gradientIdPrefix}Shadow2)`}
      />

      {/* Lime-green outer frame */}
      <path
        d="M401.65 41.8901H83.68C63.3062 41.8901 46.79 58.4064 46.79 78.7801V379.55C46.79 399.924 63.3062 416.44 83.68 416.44H401.65C422.024 416.44 438.54 399.924 438.54 379.55V78.7801C438.54 58.4064 422.024 41.8901 401.65 41.8901Z"
        fill={frame}
      />

      {/* Lime-green inner frame */}
      <path
        d="M368.7 82.3701H116.6C101.418 82.3701 89.11 94.6778 89.11 109.86V348.47C89.11 363.652 101.418 375.96 116.6 375.96H368.7C383.882 375.96 396.19 363.652 396.19 348.47V109.86C396.19 94.6778 383.882 82.3701 368.7 82.3701Z"
        fill={frame}
      />

      {/* Inner sections - Quadrants */}
      <path
        d="M381.55 233.3V329.37C381.55 349.64 365.11 366.09 344.83 366.09H242.66V233.31H381.54L381.55 233.3Z"
        fill={innerColor2}
      />
      <path
        d="M103.79 233.3H242.66V366.07H140.5C120.23 366.07 103.78 349.64 103.78 329.36V233.29L103.79 233.3Z"
        fill={innerColor2}
      />
      <path
        d="M103.78 229.17H242.66V361.94H140.67C120.31 361.94 103.78 345.41 103.78 325.05V229.17Z"
        fill={innerColor1}
      />
      <path
        d="M381.54 229.17L242.66 229.17L242.66 96.3902L344.65 96.3902C365.01 96.3902 381.54 112.92 381.54 133.28V229.17Z"
        fill={innerColor1}
      />
      <path
        d="M242.66 96.3901V229.17H103.78V133.28C103.78 112.92 120.31 96.3901 140.67 96.3901H242.66Z"
        fill={innerColor3}
      />
      <path
        d="M242.66 361.95V229.17H381.54V325.06C381.54 345.42 365.01 361.95 344.65 361.95H242.66Z"
        fill={innerColor3}
      />

      {/* Pawn spots - 4 circles in each quadrant (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          <circle cx="173.24" cy="162.5" r="35" fill={innerColor2} />
          <circle cx="173.24" cy="165.18" r="35" fill={pawnSpot} />

          <circle cx="312.11" cy="161.16" r="35" fill={innerColor2} />
          <circle cx="312.11" cy="163.84" r="35" fill={pawnSpot} />

          <circle cx="173.23" cy="293.82" r="35" fill={innerColor2} />
          <circle cx="173.23" cy="296.51" r="35" fill={pawnSpot} />

          <circle cx="312.11" cy="294.49" r="35" fill={innerColor2} />
          <circle cx="312.11" cy="297.18" r="35" fill={pawnSpot} />
        </>
      )}

      {/* Left badge display when player has voluntarily left the game */}
      {hasLeft && (
        <g>
          {/* Circle background - grey tint */}
          <circle cx="242.66" cy="229" r="100" fill="#666" />
          <circle cx="242.66" cy="229" r="85" fill="#444" />

          {/* Exit/Leave door icon - simple SVG */}
          <g transform="translate(182.66, 169)">
            {/* Door frame */}
            <rect
              x="15"
              y="10"
              width="50"
              height="100"
              rx="4"
              fill="#888"
              stroke="#aaa"
              strokeWidth="3"
            />
            {/* Door */}
            <rect x="20" y="15" width="40" height="90" rx="2" fill="#666" />
            {/* Door handle */}
            <circle cx="52" cy="60" r="4" fill="#ccc" />
            {/* Arrow pointing out */}
            <path
              d="M75 60 L100 60 M90 50 L100 60 L90 70"
              stroke="#fff"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </g>
      )}

      {/* Rank badge display when player has finished (but not left) */}
      {playerRank !== undefined && !hasLeft && (
        <g>
          {/* Trophy circle background */}
          <circle cx="242.66" cy="229" r="100" fill={pawnSpot} />
          <circle cx="242.66" cy="229" r="90" fill={innerColor1} />

          {/* Trophy icon and rank text */}
          <text
            x="242.66"
            y="210"
            textAnchor="middle"
            fontSize="72"
            aria-label={`Rank ${playerRank.toString()} trophy`}
          >
            {getRankIcon(playerRank)}
          </text>
          <text x="242.66" y="280" textAnchor="middle" fontSize="48" fontWeight="bold" fill="#333">
            {getRankOrdinal(playerRank)}
          </text>
        </g>
      )}

      {/* Decorative grass elements */}
      <g fill={grassGreen}>
        <path d="M116.633 66.8678L115.712 70.5752C115.659 70.7896 115.79 71.0066 116.004 71.0598L116.111 71.0863C116.325 71.1396 116.542 71.009 116.596 70.7946L117.516 67.0872C117.57 66.8728 117.439 66.6558 117.225 66.6026L117.118 66.5761C116.904 66.5228 116.687 66.6534 116.633 66.8678Z" />
        <path d="M114.43 66.5602V69.7602C114.43 69.9811 114.609 70.1602 114.83 70.1602H114.94C115.161 70.1602 115.34 69.9811 115.34 69.7602V66.5602C115.34 66.3392 115.161 66.1602 114.94 66.1602H114.83C114.609 66.1602 114.43 66.3392 114.43 66.5602Z" />
        <path d="M112.597 68.2287L113.403 71.3255C113.459 71.5393 113.678 71.6675 113.891 71.6119L113.998 71.5842C114.212 71.5285 114.34 71.3101 114.284 71.0963L113.478 67.9995C113.422 67.7857 113.204 67.6575 112.99 67.7132L112.884 67.7409C112.67 67.7965 112.542 68.0149 112.597 68.2287Z" />
        <path d="M376.162 93.9784L377.083 97.6858C377.137 97.9002 377.354 98.0308 377.568 97.9775L377.675 97.951C377.889 97.8978 378.02 97.6808 377.966 97.4664L377.046 93.7591C376.992 93.5447 376.775 93.414 376.561 93.4673L376.454 93.4938C376.24 93.5471 376.109 93.764 376.162 93.9784Z" />
        <path d="M378.35 93.4701V96.6701C378.35 96.891 378.529 97.0701 378.75 97.0701H378.86C379.081 97.0701 379.26 96.891 379.26 96.6701V93.4701C379.26 93.2491 379.081 93.0701 378.86 93.0701H378.75C378.529 93.0701 378.35 93.2491 378.35 93.4701Z" />
        <path d="M68.7647 353.212L69.8953 357.763C69.9605 358.026 70.2263 358.186 70.489 358.121L70.6151 358.09C70.8778 358.024 71.0378 357.759 70.9725 357.496L69.8419 352.944C69.7767 352.682 69.5109 352.522 69.2482 352.587L69.1221 352.618C68.8594 352.683 68.6994 352.949 68.7647 353.212Z" />
        <path d="M71.42 352.61V356.53C71.42 356.801 71.6394 357.02 71.91 357.02H72.04C72.3106 357.02 72.53 356.801 72.53 356.53V352.61C72.53 352.339 72.3106 352.12 72.04 352.12H71.91C71.6394 352.12 71.42 352.339 71.42 352.61Z" />
        <path d="M414.508 379.281L413.768 382.261C413.726 382.432 413.83 382.606 414.002 382.648L414.089 382.67C414.26 382.713 414.434 382.608 414.477 382.437L415.217 379.457C415.259 379.286 415.155 379.112 414.983 379.07L414.896 379.048C414.724 379.005 414.551 379.11 414.508 379.281Z" />
        <path d="M412.73 379.02V381.59C412.73 381.767 412.873 381.91 413.05 381.91H413.14C413.317 381.91 413.46 381.767 413.46 381.59V379.02C413.46 378.843 413.317 378.7 413.14 378.7H413.05C412.873 378.7 412.73 378.843 412.73 379.02Z" />
      </g>

      {/* Render pawns conditionally (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          {renderPawnAtPosition(
            'pawn1',
            pawn1,
            PawnComponent,
            {
              isActive: pawn1Active,
              isHovered: pawn1Hovered,
              isSelected: pawn1Selected,
              isMoveable: pawn1Moveable
            },
            onPawn1Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn2',
            pawn2,
            PawnComponent,
            {
              isActive: pawn2Active,
              isHovered: pawn2Hovered,
              isSelected: pawn2Selected,
              isMoveable: pawn2Moveable
            },
            onPawn2Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn3',
            pawn3,
            PawnComponent,
            {
              isActive: pawn3Active,
              isHovered: pawn3Hovered,
              isSelected: pawn3Selected,
              isMoveable: pawn3Moveable
            },
            onPawn3Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn4',
            pawn4,
            PawnComponent,
            {
              isActive: pawn4Active,
              isHovered: pawn4Hovered,
              isSelected: pawn4Selected,
              isMoveable: pawn4Moveable
            },
            onPawn4Click,
            shouldAnimatePawns
          )}
        </>
      )}
    </svg>
  );
});

export default BaseComponent;
